import React, { useState } from "react";
import MarketingLayout from "../components/MarketingLayout";

const SECTIONS = [
  {
    id: "start", title: "Getting Started",
    content: [
      {
        heading: "Opening DPL Jade",
        body: `Navigate to dplhomestar.com and click "Open App". DPL Jade runs entirely in your browser — no installation required. On first load you will see a blank dark canvas with a toolbar at the top and the Inspector panel on the right.`,
      },
      {
        heading: "The canvas",
        body: `The canvas represents your floor plan in millimetres (mm). The grid snaps to 100 mm increments. A status bar at the bottom shows your cursor position in centimetres, the current mode, and the zoom level.`,
      },
      {
        heading: "Navigation",
        body: `Pan: hold Alt and drag, or press and drag the middle mouse button.\nZoom: scroll the mouse wheel.\nReset view: refresh the page (your work is auto-saved).`,
      },
    ],
  },
  {
    id: "walls", title: "Drawing Walls",
    content: [
      {
        heading: "Entering wall mode",
        body: `Press W or click the ▬ button in the toolbar. The cursor changes to a crosshair grid icon.`,
      },
      {
        heading: "Placing walls",
        body: `Click once to set the starting point. A blue dashed preview line follows your cursor showing the wall length in real time. Click again to place the wall. The mode stays active so you can immediately start the next wall from the same point — this is called chain drawing.\n\nDouble-click or press Escape to end the chain.`,
      },
      {
        heading: "Snap points",
        body: `DPL Jade snaps to four types of points:\n• Node (cyan circle) — an existing wall endpoint.\n• Midpoint (green triangle) — the centre of an existing wall.\n• Edge (purple circle) — any point along a wall.\n• Intersection (amber X) — where two walls would cross.\n\nSnap indicators appear next to the cursor. The snap type is shown in the status bar.`,
      },
      {
        heading: "Auto-intersect",
        body: `When you draw a wall that crosses an existing wall, DPL Jade automatically splits both walls at the crossing point and creates a proper junction node. No manual trimming is needed.`,
      },
      {
        heading: "Material & height",
        body: `Before drawing, choose a material from the first dropdown in the toolbar (Brick Masonry is the default) and a wall height from the second dropdown (default 3 m). These apply to the next wall you draw.\n\nAvailable materials: Brick Masonry · AAC Block · Hollow Block · Drywall Partition · Glass Partition · RC / Concrete.`,
      },
    ],
  },
  {
    id: "select", title: "Selecting & Editing",
    content: [
      {
        heading: "Select mode",
        body: `Press S or click ↖ in the toolbar. Click any wall, room, node, or opening to select it. The Inspector panel on the right will show its properties.`,
      },
      {
        heading: "Editing a wall",
        body: `Select a wall to see its length, thickness, and height. Click any material button to change the material (the thickness updates automatically). Click a height button (2.7 m · 3 m · 3.6 m · 4.2 m) to change the wall height. Changes are instant and the cost recalculates live.`,
      },
      {
        heading: "Editing length (dimension labels)",
        body: `In any mode, click the blue dimension label above a wall to open the inline editor. Type a new length in mm and press Enter. The far end of the wall will move to match. Press Escape to cancel.`,
      },
      {
        heading: "Dragging nodes",
        body: `In Select mode, click a node (the small circles at wall endpoints) and drag it to reposition. The connected walls stretch or compress with it. Release the mouse to commit.`,
      },
      {
        heading: "Deleting elements",
        body: `Select a wall, opening or node, then press Delete or Backspace. The delete button also appears in the Inspector panel.`,
      },
    ],
  },
  {
    id: "rooms", title: "Labelling Rooms",
    content: [
      {
        heading: "Room mode",
        body: `Press R or click the 🏷 button. A hint appears: "Click inside a room to assign type". Click anywhere inside an enclosed area (a room bounded by walls).`,
      },
      {
        heading: "Assigning a type",
        body: `A popup appears at the click point. Choose a room type: Room · Bedroom · Living Room · Kitchen · Bathroom · Toilet · Store · Corridor · Office · Balcony. Optionally type a custom name (e.g. "Master Bedroom"). Click Apply.`,
      },
      {
        heading: "Room colours",
        body: `Each room type has a distinct colour and icon displayed in the canvas. The room area (m²) is shown at the centroid.`,
      },
      {
        heading: "Room costs",
        body: `Assigning a type automatically applies standard floor and wall-finish rates for that room type. These are shown in the Inspector → Props tab when the room is selected, and roll up into the total in the Cost tab.`,
      },
    ],
  },
  {
    id: "openings", title: "Doors & Windows",
    content: [
      {
        heading: "Opening mode",
        body: `Press O or click 🚪. A hint appears. Click any wall to open the placement popup.`,
      },
      {
        heading: "Presets",
        body: `Choose from: Door 900×2100 · Door 1200×2100 · Window 1200×1200 · Window 1800×1200 · Window 600×600. Then click ⊕ Place. The opening is placed at the midpoint of the wall.`,
      },
      {
        heading: "Effect on BOQ",
        body: `Openings automatically deduct their void volume from the wall's masonry calculation. A window with a 900 mm sill also reduces the net masonry volume accordingly.`,
      },
      {
        heading: "Deleting an opening",
        body: `Select the opening (S mode, click the door/window symbol on the wall) and press Delete, or click "Delete opening" in the Inspector.`,
      },
    ],
  },
  {
    id: "cost", title: "Cost & BOQ",
    content: [
      {
        heading: "The Cost tab",
        body: `Open the Inspector → Cost tab at any time to see a live breakdown. Three levels of disclosure:\n\n1. Total Project Cost (header) — one number at a glance.\n2. Structure vs Finishes — collapsible categories.\n3. Per-wall breakdown — every wall's volume, bricks, cement, sand, material cost and labour cost.`,
      },
      {
        heading: "How wall costs are calculated",
        body: `Net volume (m³) = wall length × thickness × height − opening voids.\nMaterial cost = net volume × rate/m³ (or rate/m² for glass).\nLabour cost = material cost × 25%.\nWall total = material + labour.`,
      },
      {
        heading: "How room costs are calculated",
        body: `Floor cost = floor area (m²) × floor rate for that room type.\nWall finish cost = perimeter × wall height × finish rate for that room type.\nRoom total = floor + wall finish.`,
      },
      {
        heading: "Raw materials",
        body: `The "Raw materials" section shows aggregate bricks, cement bags, and sand in cft across all brick/block walls — useful for procurement planning.`,
      },
      {
        heading: "PDF export",
        body: `Click "📄 Print / Export PDF" in the Cost tab to open the browser print dialog. A formatted report with room schedule, BOQ table, and cost summary is rendered — optimised for A4 paper. Save as PDF from the print dialog.`,
      },
    ],
  },
  {
    id: "heal", title: "Auto-Heal",
    content: [
      {
        heading: "What is auto-heal?",
        body: `DPL Jade maintains a Doubly Connected Edge List (DCEL) — a precise graph of all nodes, edges and faces. After every action the healer runs automatically to keep this graph valid.`,
      },
      {
        heading: "What it fixes",
        body: `• Duplicate nodes within 80 mm of each other — merged.\n• Collinear junctions (a node in the middle of a straight wall) — dissolved.\n• Micro-edges shorter than 50 mm — removed.\n• Face topology — rebuilt so room detection stays accurate.`,
      },
      {
        heading: "Manual heal",
        body: `Press H or click the ⚕ button. If there are graph errors the button turns amber and shows the error count. The heal log (accessible from the header badge) lists every correction made in this session.`,
      },
      {
        heading: "Topology tab",
        body: `Inspector → Topo tab shows Vertices (V), Edges (E), Half-Edges (HE), and Faces (F). The Euler number V−E+F should be 1 (planar) or 2 (with outer face) for a valid plan. Errors are listed with their type and a one-click Auto-Heal button.`,
      },
    ],
  },
  {
    id: "history", title: "Undo / Redo & History",
    content: [
      {
        heading: "Undo / Redo",
        body: `Ctrl+Z to undo, Ctrl+Y (or Ctrl+Shift+Z) to redo. Up to 60 steps are kept. The Undo and Redo buttons in the Inspector footer show whether actions are available.`,
      },
      {
        heading: "Node drag grouping",
        body: `Dragging a node is treated as a single undo step — no matter how far you move it, one Undo snaps it back to its original position.`,
      },
      {
        heading: "Cloud snapshots (Pro/Team)",
        body: `On paid plans, DPL Jade writes a snapshot to the database every time it auto-saves. Up to 20 snapshots are kept per project. Snapshot recovery UI is on the roadmap.`,
      },
    ],
  },
  {
    id: "project", title: "Project Details",
    content: [
      {
        heading: "Editing project metadata",
        body: `Click the project name in the top header bar (the ✎ pencil appears on hover). A modal allows you to set: Project Name · Client · Address / Site. These details appear in the PDF export.`,
      },
      {
        heading: "Saving",
        body: `On the free plan, work is auto-saved to localStorage every time the state changes. The save badge in the header shows: ○ Unsaved → ◌ Saving… → ● Saved.\n\nOn Pro/Team, the same badge reflects Supabase sync status.`,
      },
    ],
  },
  {
    id: "templates", title: "Templates",
    content: [
      {
        heading: "Opening the template picker",
        body: "Press T or click the 📐 button in the toolbar. A modal opens showing all available prebuilt layouts. You can also click 'Start blank' to dismiss it and begin drawing from scratch.",
      },
      {
        heading: "Available templates",
        body: "• Studio Apartment (~30 m²) — compact single-room with bathroom and kitchen zone.\n• 1 BHK Apartment (~50 m²) — one bedroom, living, kitchen, two bathrooms.\n• 2 BHK Apartment (~80 m²) — two bedrooms, living, kitchen, corridor.\n• 3 BHK Apartment (~110 m²) — three bedrooms including master suite, living, dining, kitchen.\n• Villa Ground Floor (~150 m²) — independent house with foyer, staircase, living, dining, kitchen, ground bedroom.\n• Small Office (~60 m²) — reception, open plan, glass meeting room, manager cabin.\n• Retail / Shop (~40 m²) — open shop floor, rear store room, staff toilet.\n• Clinic / Medical Office (~70 m²) — waiting area, consultation rooms, dispensary, toilets.",
      },
      {
        heading: "After loading a template",
        body: "The template loads as a fully editable plan — every wall, room label, and dimension can be modified. Switch to Wall mode (W) to add walls, Select mode (S) to drag nodes, or Room mode (R) to re-label rooms. Use the dimension labels to resize any wall to your exact specifications.",
      },
      {
        heading: "Loading a template over an existing plan",
        body: "Loading a template replaces the current canvas. Your previous state is saved to the undo stack — press Ctrl+Z immediately if you change your mind.",
      },
    ],
  },
  {
    id: "keyboard", title: "Keyboard Shortcuts",
    content: [
      {
        heading: "Full reference",
        body: [
          ["W", "Wall draw mode"],
          ["S", "Select / drag mode"],
          ["R", "Label room mode"],
          ["O", "Add opening mode"],
          ["T", "Open template picker"],
          ["H", "Auto-heal graph"],
          ["C", "Clear canvas (irreversible without undo)"],
          ["Ctrl+Z", "Undo"],
          ["Ctrl+Y / Ctrl+Shift+Z", "Redo"],
          ["Delete / Backspace", "Delete selected element"],
          ["Escape", "Cancel current action / close popups"],
          ["Alt + drag", "Pan canvas"],
          ["Scroll wheel", "Zoom in / out"],
          ["Click dimension label", "Edit wall length inline"],
        ],
      },
    ],
  },
];

function ShortcutTable({ rows }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16 }}>
      <tbody>
        {rows.map(([key, desc]) => (
          <tr key={key} style={{ borderBottom: "1px solid #0d1420" }}>
            <td style={{ padding: "8px 12px 8px 0", width: 180 }}>
              <code style={{ background: "#0d1320", border: "1px solid #1e2a36", color: "#3b82f6", padding: "2px 8px", borderRadius: 4, fontSize: 12, fontFamily: "'JetBrains Mono',monospace" }}>{key}</code>
            </td>
            <td style={{ padding: "8px 0", fontSize: 13, color: "#6b7e94" }}>{desc}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function UserGuide() {
  const [active, setActive] = useState("start");
  const section = SECTIONS.find(s => s.id === active);

  return (
    <MarketingLayout>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "48px 24px 80px", display: "flex", gap: 48, alignItems: "flex-start" }}>

        {/* Sidebar */}
        <nav style={{ width: 200, flexShrink: 0, position: "sticky", top: 80 }}>
          <div style={{ fontSize: 9, color: "#2a3a4a", letterSpacing: 2, fontWeight: 800, marginBottom: 14, fontFamily: "'JetBrains Mono',monospace" }}>USER GUIDE</div>
          {SECTIONS.map(s => (
            <button key={s.id} onClick={() => setActive(s.id)}
              style={{
                display: "block", width: "100%", textAlign: "left", padding: "8px 10px",
                background: active === s.id ? "#0c1828" : "transparent",
                border: active === s.id ? "1px solid #1e3d66" : "1px solid transparent",
                borderRadius: 5, color: active === s.id ? "#60a5fa" : "#3d5068",
                fontSize: 13, cursor: "pointer", marginBottom: 2, fontFamily: "inherit",
              }}>
              {s.title}
            </button>
          ))}
        </nav>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontSize: "clamp(22px,3vw,32px)", fontWeight: 800, color: "#dde6f0", marginBottom: 32 }}>{section.title}</h1>
          {section.content.map((block, i) => (
            <div key={i} style={{ marginBottom: 32 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: "#8496a8", marginBottom: 10 }}>{block.heading}</h3>
              {Array.isArray(block.body) ? (
                <ShortcutTable rows={block.body} />
              ) : (
                block.body.split("\n").map((line, j) => (
                  line.trim() === ""
                    ? <br key={j} />
                    : <p key={j} style={{ fontSize: 14, color: "#6b7e94", lineHeight: 1.85, margin: "0 0 10px" }}>{line}</p>
                ))
              )}
            </div>
          ))}

          {/* Prev / Next */}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 40, paddingTop: 24, borderTop: "1px solid #131924" }}>
            {SECTIONS.findIndex(s => s.id === active) > 0 ? (
              <button onClick={() => setActive(SECTIONS[SECTIONS.findIndex(s => s.id === active) - 1].id)}
                style={{ background: "transparent", border: "1px solid #131924", color: "#4a5e70", padding: "8px 16px", borderRadius: 5, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>
                ← {SECTIONS[SECTIONS.findIndex(s => s.id === active) - 1].title}
              </button>
            ) : <span />}
            {SECTIONS.findIndex(s => s.id === active) < SECTIONS.length - 1 ? (
              <button onClick={() => setActive(SECTIONS[SECTIONS.findIndex(s => s.id === active) + 1].id)}
                style={{ background: "#0c1828", border: "1px solid #1e3d66", color: "#60a5fa", padding: "8px 16px", borderRadius: 5, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>
                {SECTIONS[SECTIONS.findIndex(s => s.id === active) + 1].title} →
              </button>
            ) : <span />}
          </div>
        </div>
      </div>
    </MarketingLayout>
  );
}
